import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import {
  AbstractOryMessageContextFormatter,
  OryMessageContextHandlerMap,
} from '@shopware/iam-ory';

/**
 * Application-specific Ory message context formatter.
 *
 * Extends iam-ory's abstract formatter to provide human-readable formatting
 * of Ory message context values (dates, timestamps, etc.) for display in UI.
 *
 * Handles context formatting for:
 * - Flow expiration dates (all flow types)
 * - Privileged session expiration
 * - Secret usage timestamps
 *
 * Uses Angular's formatDate with current locale for internationalization.
 */
@Injectable({
  providedIn: 'root'
})
export class OryMessageContextFormatter extends AbstractOryMessageContextFormatter {

  /**
   * Handler registry for message context formatting
   *
   * Each handler receives the typed context for its message and returns
   * the formatted context with the same structure but human-readable values.
   */
  protected readonly handlers: Partial<OryMessageContextHandlerMap> = {
    // Privileged session expiration (recovery flow)
    RECOVERY_SUCCESS_CHANGE_PASSWORD: (ctx, locale) => ({
      ...ctx,
      privilegedSessionExpiresAt: this.#formatDate(ctx.privilegedSessionExpiresAt, locale),
      privileged_session_expires_at: this.#formatDate(ctx.privileged_session_expires_at, locale),
    }),

    // Flow expiration messages - all use the same handler (shared context type)
    LOGIN_FLOW_EXPIRED: (ctx, locale) => ({
      ...ctx,
      expired_at: this.#formatDate(ctx.expired_at, locale),
      // Keep unix timestamp as-is
    }),
    REGISTRATION_FLOW_EXPIRED: (ctx, locale) => ({
      ...ctx,
      expired_at: this.#formatDate(ctx.expired_at, locale),
    }),
    SETTINGS_FLOW_EXPIRED: (ctx, locale) => ({
      ...ctx,
      expired_at: this.#formatDate(ctx.expired_at, locale),
    }),
    RECOVERY_FLOW_EXPIRED: (ctx, locale) => ({
      ...ctx,
      expired_at: this.#formatDate(ctx.expired_at, locale),
    }),
    VERIFICATION_FLOW_EXPIRED: (ctx, locale) => ({
      ...ctx,
      expired_at: this.#formatDate(ctx.expired_at, locale),
    }),

    // Secret usage timestamp
    SECRET_USED_AT_INFO: (ctx, locale) => ({
      ...ctx,
      used_at: this.#formatDate(ctx.used_at, locale),
      // Keep unix timestamp as-is
    }),
  };

  #formatDate(isoString: string, locale: string): string {
    try {
      return formatDate(isoString, 'medium', locale);
    } catch (error) {
      // Graceful fallback if formatting fails (invalid date, etc.)
      console.warn('Failed to format date:', isoString, error);
      return isoString;
    }
  }
}

